import { useMemo } from "react";
import { useResponsiveDisplayMode } from "../useResponsiveDisplayMode";
import { pickValueForDisplayMode } from "./pickValueForDisplayMode";
import { ResponsiveDeclarationSet } from "./ResponsiveDeclarationSet";
import { ResponsiveValues } from "./ResponsiveValues";

/**
 * Provides values for a given definition based on the current display mode.
 * @param declarations The responsive declarations.
 * @returns The object containing the values for the current display mode.
 */
export function useResponsive<TDeclaration extends ResponsiveDeclarationSet<string>>(
  declarations: TDeclaration,
): ResponsiveValues<TDeclaration> {

  const displayMode = useResponsiveDisplayMode();

  return useMemo((): ResponsiveValues<TDeclaration> => {
    const result: Partial<Record<keyof TDeclaration, unknown>> = {};

    for (const key of Object.keys(declarations)) {
      const values = declarations[key as keyof TDeclaration];
      if (!values) continue;

      result[key as keyof TDeclaration] = pickValueForDisplayMode<unknown>(values, displayMode);
    }

    return result as ResponsiveValues<TDeclaration>;
  }, [displayMode, declarations]);
}
